import { hexDist, hexLen, hexToWorld, ROW, type Hex } from '../hex.ts';
import { iatan2, idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';

/**
 * Nadd's workshop on the Flank Cliff (docs/sendas/artifice.md, chapter 1): the grounds, the same from the seed in the
 * sim and the drawing, integers only. Up is −y (the top of the screen).
 *
 * - `corniceLayout` (act 1, the goat path): a ledge two hexes wide winding up the cliff from the foot of the board to the
 *   workshop's door at its top; the wall on the west (−x), the drop to the sea on the east. Nests cut into the wall
 *   (one open cell off the ledge, a step up: the goblin slingers throw from there), and rests (a row three wide, a
 *   bench of stone) where Grillo crawls to when he falls.
 * - `workshopLayout` (act 2, the sleeping workshop): five halls one below the other, each nine hexes wide and five
 *   rows tall, joined by a stair two hexes wide at alternate ends (the way down zigzags through every hall); the old
 *   golems' posts along each hall's far wall, facing into it; the grille at each stair; the lift's landing at the end.
 * - `gearLayout` (act 3, the test): the lift's hall, round; in its middle the gear bed, axles on a coarse triangular
 *   lattice two hexes apart (a gear on each meshes with the gear of every axle next to it); the water wheel's axle at
 *   the west end, the lift drum's at the east, the gears lying about the hall, the golems' posts round the wall and
 *   the Overseer's by the lift. `gearSolve` counts every way of setting the pieces that turns the drum right.
 */

export const CORNICE_RADIUS = 12;
export const WORKSHOP_RADIUS = 15;
export const GEAR_RADIUS = 8;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;

/* ---------- the goat path ---------- */

export interface CorniceLayout {
  radius: number;
  /** What can't be walked: the wall (west), the drop (east), the rock past the door. */
  rocks: Hex[];
  /** For the drawing: the wall's cells and the drop's. */
  wall: Hex[];
  drop: Hex[];
  /** The ledge's open cells, its nests (a step up into the wall), its rests' extra cells. */
  ledge: Hex[];
  nests: Hex[];
  rests: Hex[];
  /** The ledge's middle, row by row from the foot to the door (sim units). */
  way: { x: number; y: number }[];
  /** Where Grillo can lie and get up again: indexes into `way`, from the foot (the last is by the door). */
  restAt: number[];
  /** The nests in world points, and the index of the way's row each is on. */
  nestAt: { x: number; y: number; row: number }[];
  door: { x: number; y: number };
  start: { x: number; y: number };
}

/** The goat path from the seed. */
export const corniceLayout = (seed: number): CorniceLayout => {
  const R = CORNICE_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc0a1));
  // The middle's x on each row, from the foot (r = R − 1) up to the door (r = −(R − 1)): it winds, a half hex a row.
  const mid = new Map<number, number>();
  let x = rngRange(rng, -2, 2) * 500;
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let r = R - 1; r >= -(R - 1); r--) {
    mid.set(r, x);
    if (rngInt(rng, 10) < 3) dir = -dir;
    x = imax(-3500, imin(3500, x + dir * 500));
    if (Math.abs(x) === 3500) dir = -dir;
  }
  // The rests: pairs of rows three wide (the wall cut back a hex), every six rows or so from the foot.
  const restRows = new Set<number>();
  for (let r = R - 5; r > -(R - 3); r -= rngRange(rng, 6, 7)) {
    restRows.add(r);
    restRows.add(r - 1);
  }
  // A nest every three or four rows, never on a rest's rows, never the first rows nor the last.
  const nestRows = new Set<number>();
  for (let r = R - 3 - rngInt(rng, 2); r > -(R - 3); r -= 3) nestRows.add(restRows.has(r) ? r - 2 : r);
  // Each row's first cell (the wall's side of the ledge): at or just west of the winding middle.
  const first = (r: number): number => {
    const m = mid.get(r) as number;
    const off = (((r * 500) % 1000) + 1000) % 1000;
    return m - ((((m - off) % 1000) + 1000) % 1000);
  };
  const ledge: Hex[] = [];
  const nests: Hex[] = [];
  const rests: Hex[] = [];
  const wall: Hex[] = [];
  const drop: Hex[] = [];
  const rocks: Hex[] = [];
  for (const h of allCells(R)) {
    const m = mid.get(h.r);
    const p = hexToWorld(h.q, h.r);
    if (m === undefined) {
      rocks.push(h);
      if (p.x > (mid.get(h.r > 0 ? R - 1 : -(R - 1)) ?? 0)) drop.push(h);
      else wall.push(h);
      continue;
    }
    const cx = first(h.r);
    if (p.x === cx || p.x === cx + 1000) {
      ledge.push(h);
      continue;
    }
    if (p.x === cx - 1000 && restRows.has(h.r)) {
      rests.push(h);
      continue;
    }
    if (p.x === cx - 1000 && nestRows.has(h.r)) {
      nests.push(h);
      continue;
    }
    if (p.x < cx) wall.push(h);
    else drop.push(h);
    rocks.push(h);
  }
  const way: { x: number; y: number }[] = [];
  const restAt: number[] = [];
  const rows: number[] = [];
  for (let r = R - 1; r >= -(R - 1); r--) rows.push(r);
  rows.forEach((r, i) => {
    way.push({ x: first(r) + 500, y: r * ROW });
    // A rest is its pair's upper row.
    if (restRows.has(r) && !restRows.has(r - 1)) restAt.push(i);
  });
  // The last rest: by the door.
  restAt.push(way.length - 2);
  const nestAt = nests
    .slice()
    .sort(sortHex)
    .reverse()
    .map((n) => ({ ...hexToWorld(n.q, n.r), row: rows.indexOf(n.r) }));
  rocks.sort(sortHex);
  wall.sort(sortHex);
  drop.sort(sortHex);
  ledge.sort(sortHex);
  nests.sort(sortHex);
  rests.sort(sortHex);
  const top = way[way.length - 1] as { x: number; y: number };
  const foot = way[0] as { x: number; y: number };
  return { radius: R, rocks, wall, drop, ledge, nests, rests, way, restAt, nestAt, door: { x: top.x, y: top.y - ROW }, start: { x: foot.x, y: foot.y } };
};

/* ---------- the sleeping workshop ---------- */

/** The rows a hall spans from its middle row, and its half width (sim units). */
export const HALL_HALF_ROWS = 2;
const HALL_HALF_W = 4000;
/** Each hall's middle row, from the top. */
const HALL_ROWS = [-12, -6, 0, 6, 12];

export interface WorkshopHall {
  /** Its middle (sim units). */
  at: { x: number; y: number };
  /** Its cells. */
  cells: Hex[];
  /** The way in (from the hall above, or the door) and the way out (the stair down, or the lift), sim units. */
  in: { x: number; y: number };
  out: { x: number; y: number };
  /** The stair's two cells down out of it (its grille): none for the last. */
  stair: Hex[];
  /** The old golems' posts along its far wall, each with the way it faces (angle steps, 1024 a turn). */
  posts: { x: number; y: number; face: number }[];
  /** Its cold forges and its hanging chains (for the drawing): cells by the walls. */
  forges: Hex[];
}

export interface WorkshopLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  halls: WorkshopHall[];
  start: { x: number; y: number };
  /** The lift's landing, at the last hall's far end. */
  lift: { x: number; y: number };
}

/** How many golems each hall keeps (from the top). */
export const WORKSHOP_GOLEMS = [1, 2, 2, 2, 3];

/** The hex on row `r` whose centre is nearest world x. */
const nearestOn = (x: number, r: number): Hex => {
  const v = x - r * 500 + 500;
  return { q: (v - (((v % 1000) + 1000) % 1000)) / 1000, r };
};

/** The sleeping workshop from the seed: five halls, the way down zigzagging through each. */
export const workshopLayout = (seed: number): WorkshopLayout => {
  const R = WORKSHOP_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x7a11));
  // The first hall's way out is east or west (from the seed); then each alternates.
  const east0 = rngInt(rng, 2) === 0;
  const open = new Set<string>();
  const halls: WorkshopHall[] = [];
  HALL_ROWS.forEach((row, i) => {
    const cx = rngRange(rng, -1, 1) * 1000;
    const cells: Hex[] = [];
    for (let r = row - HALL_HALF_ROWS; r <= row + HALL_HALF_ROWS; r++) {
      // A little rounder at the top and bottom rows (one hex less each side).
      const half = Math.abs(r - row) === HALL_HALF_ROWS ? HALL_HALF_W - 1000 : HALL_HALF_W;
      for (let q = -R; q <= R; q++) {
        if (hexLen(q, r) >= R) continue;
        const p = hexToWorld(q, r);
        if (p.x < cx - half || p.x > cx + half) continue;
        cells.push({ q, r });
        open.add(`${q},${r}`);
      }
    }
    const east = (i % 2 === 0) === east0;
    const at = { x: cx, y: row * ROW };
    const prev = halls[i - 1];
    const inX = prev ? prev.out.x : cx + (east ? -1 : 1) * (HALL_HALF_W - 1500);
    const outX = cx + (east ? 1 : -1) * (HALL_HALF_W - 1500);
    halls.push({ at, cells: cells.sort(sortHex), in: { x: inX, y: (row - HALL_HALF_ROWS) * ROW }, out: { x: outX, y: (row + HALL_HALF_ROWS) * ROW }, stair: [], posts: [], forges: [] });
  });
  // The stairs: two cells on the row between a hall and the next, under its way out (toward its middle).
  for (let i = 0; i < halls.length - 1; i++) {
    const a = halls[i] as WorkshopHall;
    const row = (HALL_ROWS[i] as number) + HALL_HALF_ROWS + 1;
    const h0 = nearestOn(a.out.x, row);
    const toward = a.out.x > a.at.x ? -1 : 1;
    a.stair = [h0, { q: h0.q + toward, r: row }].sort(sortHex);
    for (const h of a.stair) open.add(`${h.q},${h.r}`);
  }
  // The old golems' posts along each hall's top wall, facing down into it (+y), spread across it, two hexes clear of
  // its way in and its way out; two cold forges on its bottom wall.
  halls.forEach((h, i) => {
    const n = WORKSHOP_GOLEMS[i] as number;
    const top = (HALL_ROWS[i] as number) - HALL_HALF_ROWS;
    const along = h.cells.filter((c) => c.r === top).map((c) => hexToWorld(c.q, c.r));
    const ok = along.filter((p) => Math.abs(p.x - h.in.x) > 1800 && Math.abs(p.x - h.out.x) > 1800);
    const pick = ok.length >= n ? ok : along;
    for (let k = 0; k < n; k++) {
      const p = pick[imin(pick.length - 1, idiv((k * 2 + 1) * pick.length, 2 * n))] as { x: number; y: number };
      h.posts.push({ x: p.x, y: p.y, face: 256 });
    }
    for (const c of h.cells.filter((x) => x.r === top + 2 * HALL_HALF_ROWS)) {
      if (h.forges.length >= 2) break;
      const p = hexToWorld(c.q, c.r);
      if (Math.abs(p.x - h.in.x) < 1600 || Math.abs(p.x - h.out.x) < 1600) continue;
      if (h.forges.some((f) => hexDist(f, c) < 3)) continue;
      h.forges.push(c);
    }
  });
  const rocks: Hex[] = [];
  const floor: Hex[] = [];
  for (const h of allCells(R)) {
    if (open.has(`${h.q},${h.r}`)) floor.push(h);
    else rocks.push(h);
  }
  const first = halls[0] as WorkshopHall;
  const last = halls[halls.length - 1] as WorkshopHall;
  return { radius: R, rocks: rocks.sort(sortHex), floor: floor.sort(sortHex), halls, start: { x: first.in.x, y: first.in.y + ROW }, lift: { x: last.out.x, y: last.at.y } };
};

/* ---------- the gear train ---------- */

/** The gears there are (teeth). */
export const GEAR_TEETH = [12, 24, 36] as const;
/** The coarse lattice's six neighbours. */
const CDIRS: readonly Hex[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];
const cAdj = (a: Hex, b: Hex): boolean => hexDist(a, b) === 1;

export interface GearLayout {
  radius: number;
  rocks: Hex[];
  /** The axles on the coarse lattice (axial, ×2 for the board's hexes), the wheel's first and the drum's second. */
  axles: Hex[];
  /** Which axles touch (each axle's neighbours, by index). */
  adj: number[][];
  /** The wheel's speed (turns a minute, clockwise), and the speed the drum asks (the other way). */
  wheel: number;
  want: number;
  /** The pieces lying about the hall (teeth), and where each lies (hexes). */
  pieces: number[];
  lie: Hex[];
  /** The golems' posts round the wall, each facing the middle (angle steps), and the Overseer's by the lift. */
  posts: { x: number; y: number; face: number }[];
  boss: { x: number; y: number; face: number };
  /** The water wheel and the drum beyond their axles (world points), the lift's landing. */
  wheelAt: { x: number; y: number };
  drumAt: { x: number; y: number };
  lift: { x: number; y: number };
  start: { x: number; y: number };
  /** Every setting of the pieces that turns the drum right (axles by index → teeth; the fewest), for the tests. */
  answers: number[][];
}

/** An axle's hex on the board. */
export const axleHex = (a: Hex): Hex => ({ q: a.q * 2, r: a.r * 2 });

/**
 * The train as set: a gear on each axle (`set[i]`, its teeth; 0 none). The wheel's axle turns at `wheel`, clockwise
 * (+1); each gear meshing with one turning turns the other way, at speed × its teeth over the other's. An axle turned
 * two ways at once jams the whole train (`jam`: the axle where it did). The drum's axle (index 1): its direction and
 * speed, 0 if nothing reaches it. Speeds are exact: wheel × the wheel gear's teeth over this one's.
 */
export interface TrainState {
  dir: number[];
  speed: number[];
  jam: number;
  drumDir: number;
  drumSpeed: number;
}

export const gearTrain = (adj: readonly (readonly number[])[], set: readonly number[], wheel: number): TrainState => {
  const n = set.length;
  const dir = new Array<number>(n).fill(0);
  const speed = new Array<number>(n).fill(0);
  let jam = -1;
  const w = set[0] as number;
  if (w > 0) {
    dir[0] = 1;
    speed[0] = wheel;
    const queue = [0];
    for (let k = 0; k < queue.length && jam < 0; k++) {
      const i = queue[k] as number;
      for (const j of adj[i] as number[]) {
        if (!(set[j] as number)) continue;
        const d = -(dir[i] as number);
        if (dir[j] === 0) {
          dir[j] = d;
          speed[j] = idiv(wheel * w, set[j] as number);
          queue.push(j);
        } else if (dir[j] !== d) {
          jam = j;
          break;
        }
      }
    }
  }
  if (jam >= 0) return { dir: dir.fill(0), speed: speed.fill(0), jam, drumDir: 0, drumSpeed: 0 };
  return { dir, speed, jam, drumDir: dir[1] as number, drumSpeed: speed[1] as number };
};

/** Whether a train turns the drum as it asks: the other way from the wheel (−1), at `want`, nothing jammed. */
export const trainRight = (t: TrainState, want: number): boolean => t.jam < 0 && t.drumDir === -1 && t.drumSpeed === want;

/**
 * Every way of setting the pieces on the axles that turns the drum right, with no gear off the train (a gear that
 * touches nothing doesn't change it): each as the teeth on each axle. Distinct trains by the axles they use.
 */
export const gearSolve = (L: Pick<GearLayout, 'adj' | 'wheel' | 'want' | 'pieces'> & { axles: Hex[] }): number[][] => {
  const n = L.axles.length;
  const out: number[][] = [];
  const seen = new Set<string>();
  const set = new Array<number>(n).fill(0);
  const used = new Array<boolean>(L.pieces.length).fill(false);
  const go = (i: number): void => {
    if (i === n) {
      const t = gearTrain(L.adj, set, L.wheel);
      if (!trainRight(t, L.want)) return;
      // Every gear set turns (none lies idle off the train).
      for (let k = 0; k < n; k++) if ((set[k] as number) && !(t.dir[k] as number)) return;
      const key = set.map((x) => (x ? 1 : 0)).join('');
      if (seen.has(key)) return;
      seen.add(key);
      out.push([...set]);
      return;
    }
    go(i + 1);
    const tried = new Set<number>();
    for (let p = 0; p < L.pieces.length; p++) {
      if (used[p] || tried.has(L.pieces[p] as number)) continue;
      tried.add(L.pieces[p] as number);
      used[p] = true;
      set[i] = L.pieces[p] as number;
      go(i + 1);
      set[i] = 0;
      used[p] = false;
    }
  };
  go(0);
  return out;
};

/**
 * The lift's hall from the seed: an axle set on the coarse lattice round its middle (the wheel's west, the drum's east,
 * one between them: the short way, one middle, that turns the drum the wheel's way), and more axles about them; the
 * pieces counted (the wheel's and the drum's gears and two for the middles, maybe one more); the wheel's speed and the
 * drum's. Kept only if one or two trains turn the drum right (`gearSolve`), always with two middles.
 */
export const gearLayout = (seed: number): GearLayout => {
  const R = GEAR_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x6ea2));
  const W: Hex = { q: -1, r: 0 };
  const D: Hex = { q: 1, r: 0 };
  const M: Hex = { q: 0, r: 0 };
  // Round the three, the coarse cells the train could use (inside the bed: no farther than two from the middle).
  const ring: Hex[] = [];
  for (const base of [W, M, D])
    for (const d of CDIRS) {
      const h = { q: base.q + d.q, r: base.r + d.r };
      if (hexLen(h.q, h.r) > 2 || hexLen(h.q * 2, h.r * 2) > 4) continue;
      if ([W, M, D].some((x) => x.q === h.q && x.r === h.r) || ring.some((x) => x.q === h.q && x.r === h.r)) continue;
      ring.push(h);
    }
  ring.sort(sortHex);
  const pairs: [number, number][] = [
    [12, 36],
    [36, 12],
    [12, 24],
    [24, 12],
    [24, 36],
    [36, 24],
  ];
  const wheels = [24, 30, 36];
  for (let tries = 0; tries < 400; tries++) {
    const extra = rngRange(rng, 3, 5);
    const pool = ring.slice();
    const axles: Hex[] = [W, D, M];
    for (let k = 0; k < extra && pool.length; k++) axles.push(pool.splice(rngInt(rng, pool.length), 1)[0] as Hex);
    // A mirror north to south, from the seed (the board looks different, the puzzle is the same kind).
    const flip = rngInt(rng, 2) === 1;
    const ax = axles.map((a) => (flip ? { q: a.q + a.r, r: (-a.r) | 0 } : a));
    const adj = ax.map((a, i) => ax.map((b, j) => (i !== j && cAdj(a, b) ? j : -1)).filter((j) => j >= 0));
    const [tw, td] = pairs[rngInt(rng, pairs.length)] as [number, number];
    const wheel = wheels[rngInt(rng, wheels.length)] as number;
    const want = idiv(wheel * tw, td);
    if ((wheel * tw) % td !== 0) continue;
    const mids = [GEAR_TEETH[rngInt(rng, 3)] as number, GEAR_TEETH[rngInt(rng, 3)] as number];
    const pieces = [tw, td, ...mids];
    if (rngInt(rng, 3) === 0) pieces.push(GEAR_TEETH[rngInt(rng, 3)] as number);
    // Shuffle, so the first piece is never the wheel's.
    for (let i = pieces.length - 1; i > 0; i--) {
      const j = rngInt(rng, i + 1);
      const t = pieces[i] as number;
      pieces[i] = pieces[j] as number;
      pieces[j] = t;
    }
    const answers = gearSolve({ axles: ax, adj, wheel, want, pieces });
    if (answers.length < 1 || answers.length > 2) continue;
    if (!answers.every((a) => a.filter((x) => x > 0).length === 4)) continue;
    // Where the pieces lie: about the hall, three to six hexes out, off the bed and apart.
    const bed = new Set<string>();
    for (const a of ax) {
      const h = axleHex(a);
      bed.add(`${h.q},${h.r}`);
      for (const d of CDIRS) bed.add(`${h.q + d.q},${h.r + d.r}`);
    }
    const lie: Hex[] = [];
    const spots = allCells(R).filter((h) => {
      const l = hexLen(h.q, h.r);
      return l >= 4 && l <= 6 && !bed.has(`${h.q},${h.r}`) && Math.abs(hexToWorld(h.q, h.r).x) < 5600;
    });
    for (let g = 0; g < 60 && lie.length < pieces.length; g++) {
      const h = spots[rngInt(rng, spots.length)] as Hex;
      if (lie.some((o) => hexDist(o, h) < 3)) continue;
      lie.push(h);
    }
    if (lie.length < pieces.length) continue;
    // The golems' posts: six round the wall, facing the middle.
    const posts: { x: number; y: number; face: number }[] = [];
    for (let k = 0; k < 6; k++) {
      const h = hexRingAt(R - 2, (k * (R - 2) + idiv(R - 2, 2)) % (6 * (R - 2)));
      const p = hexToWorld(h.q, h.r);
      posts.push({ x: p.x, y: p.y, face: (iatan2(-p.y, -p.x) + 1024) % 1024 });
    }
    const wheelAt = hexToWorld(-6, 0);
    const drumAt = hexToWorld(6, 0);
    const lift = hexToWorld(6, -2);
    const boss = { ...hexToWorld(5, 1), face: 512 };
    const rocks = allCells(R).filter((h) => hexLen(h.q, h.r) >= R || (h.r === 0 && (h.q === -6 || h.q === 6 || h.q === -7 || h.q === 7)));
    return { radius: R, rocks: rocks.sort(sortHex), axles: ax, adj, wheel, want, pieces, lie, posts, boss, wheelAt, drumAt, lift, start: hexToWorld(-2, 5), answers };
  }
  throw new Error('no gear train');
};

/** The `i`-th hex of a ring (clockwise from its east corner). */
const hexRingAt = (radius: number, i: number): Hex => {
  let h: Hex = { q: radius, r: 0 };
  const steps: Hex[] = [
    { q: -1, r: 1 },
    { q: -1, r: 0 },
    { q: 0, r: -1 },
    { q: 1, r: -1 },
    { q: 1, r: 0 },
    { q: 0, r: 1 },
  ];
  let k = 0;
  for (const s of steps)
    for (let j = 0; j < radius; j++) {
      if (k === i) return h;
      h = { q: h.q + s.q, r: h.r + s.r };
      k++;
    }
  return h;
};

